import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useUserSettings, UserSettings, DateFormatOption } from '@/hooks/useUserSettings';

interface UserSettingsContextValue {
  settings: UserSettings;
  loading: boolean;
  updateDateFormat: (dateFormat: DateFormatOption) => Promise<void>;
  updateTimezone: (timezone: string) => Promise<void>;
  updateTooltipsEnabled: (enabled: boolean) => Promise<void>;
}

const UserSettingsContext = createContext<UserSettingsContextValue | undefined>(undefined);

interface UserSettingsProviderProps {
  children: ReactNode;
}

export function UserSettingsProvider({ children }: UserSettingsProviderProps) {
  const { settings, loading, updateDateFormat, updateTimezone, updateTooltipsEnabled } = useUserSettings();

  // Every tooltip and help label reads this context. A token refresh re-renders
  // this provider through the auth context, so keep the value the same object
  // unless a setting actually changed, or every consumer would re-render too.
  const value = useMemo<UserSettingsContextValue>(
    () => ({ settings, loading, updateDateFormat, updateTimezone, updateTooltipsEnabled }),
    [settings, loading, updateDateFormat, updateTimezone, updateTooltipsEnabled],
  );

  return (
    <UserSettingsContext.Provider value={value}>
      {children}
    </UserSettingsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUserSettingsContext() {
  const context = useContext(UserSettingsContext);
  if (context === undefined) {
    // Return defaults if used outside provider
    return {
      settings: {
        dateFormat: 'dd/MM/yy' as DateFormatOption,
        timezone: 'UTC',
        tooltipsEnabled: true },
      loading: false,
      updateDateFormat: async () => {},
      updateTimezone: async () => {},
      updateTooltipsEnabled: async () => {} };
  }
  return context;
}
